import React from "react";

const CONTACT_EMAIL = "travinov@gmail.com";
const EFFECTIVE_DATE = "February 9, 2026";

export function PrivacyPage({
  theme,
  cardStyle,
  onNavigate,
}: {
  theme: "light" | "dark";
  cardStyle: React.CSSProperties;
  onNavigate: (to: string) => void;
}) {
  const muted: React.CSSProperties = {
    color: theme === "dark" ? "#9fb3d1" : "#5a6776",
    fontSize: 13,
    lineHeight: 1.55,
  };

  const h2: React.CSSProperties = { margin: 0, fontSize: 16, fontWeight: 900 };

  const linkStyle: React.CSSProperties = {
    color: "#0f8c57",
    fontWeight: 800,
    textDecoration: "none",
  };

  return (
    <div style={{ display: "grid", gap: 16, paddingTop: 10 }}>
      <div style={{ display: "grid", gap: 6 }}>
        <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.3 }}>Privacy Policy</div>
        <div style={muted}>
          Effective date: <span style={{ fontWeight: 800 }}>{EFFECTIVE_DATE}</span>
        </div>
      </div>

      <div style={{ ...cardStyle, display: "grid", gap: 10 }}>
        <div style={h2}>Summary</div>
        <div style={muted}>
          SplitwiseKiller collects and processes the minimum information needed to provide core app functionality such as
          accounts, groups, friends/contacts, expenses, and file uploads (avatars/attachments). We do not sell personal
          data and we do not use third-party advertising or analytics SDKs for tracking.
        </div>
      </div>

      <div style={{ ...cardStyle, display: "grid", gap: 12 }}>
        <div style={h2}>Information We Collect</div>
        <div style={muted}>
          We may collect the following types of information when you use the app:
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, ...muted }}>
          <li>Account information: email address, password (stored as a secure hash), display name (optional).</li>
          <li>Contacts you create in the app: email and/or phone number, local display name (optional).</li>
          <li>Expense and group data: group names/descriptions, expense amounts, currencies, participants, notes/comments.</li>
          <li>Uploaded files: avatars and other attachments you upload.</li>
          <li>Technical data: basic server logs (for example, request timing and error logs) for security and reliability.</li>
        </ul>
      </div>

      <div style={{ ...cardStyle, display: "grid", gap: 12 }}>
        <div style={h2}>How We Use Information</div>
        <ul style={{ margin: 0, paddingLeft: 18, ...muted }}>
          <li>Provide and operate the service (authentication, groups, expenses, balances, file downloads/uploads).</li>
          <li>Maintain security and prevent abuse (for example, rate limiting and fraud detection).</li>
          <li>Improve reliability (debugging and investigating errors).</li>
        </ul>
      </div>

      <div style={{ ...cardStyle, display: "grid", gap: 12 }}>
        <div style={h2}>Sharing</div>
        <div style={muted}>
          We share your data with other users only as required to provide the product features (for example, group
          members can see group expenses and avatars). We do not sell your personal information. We do not integrate
          third-party advertising networks.
        </div>
      </div>

      <div style={{ ...cardStyle, display: "grid", gap: 12 }}>
        <div style={h2}>Data Storage and Security</div>
        <div style={muted}>
          Data is stored on our servers to provide the service. We use standard security practices, including encrypted
          transport (HTTPS) and password hashing. No method of transmission or storage is 100% secure, but we work to
          protect your information.
        </div>
      </div>

      <div style={{ ...cardStyle, display: "grid", gap: 12 }}>
        <div style={h2}>Your Choices</div>
        <div style={muted}>
          You can update certain profile information in the app. If you would like to request deletion of your account
          or personal data, contact us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} style={linkStyle}>
            {CONTACT_EMAIL}
          </a>
          .
        </div>
      </div>

      <div style={{ ...cardStyle, display: "grid", gap: 12 }}>
        <div style={h2}>Children</div>
        <div style={muted}>
          The app is not directed to children under 13. If you believe a child has provided personal information, please
          contact us and we will take appropriate steps.
        </div>
      </div>

      <div style={{ ...cardStyle, display: "grid", gap: 12 }}>
        <div style={h2}>Contact</div>
        <div style={muted}>
          Email:{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} style={linkStyle}>
            {CONTACT_EMAIL}
          </a>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => onNavigate("/")}
          style={{
            padding: "10px 12px",
            borderRadius: 12,
            border: theme === "dark" ? "1px solid #273244" : "1px solid #dde4ec",
            background: theme === "dark" ? "#0e1116" : "#ffffff",
            color: theme === "dark" ? "#e7ecf3" : "#111",
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Back to app
        </button>
        <button
          type="button"
          onClick={() => onNavigate("/support")}
          style={{
            padding: "10px 12px",
            borderRadius: 12,
            border: theme === "dark" ? "1px solid #273244" : "1px solid #dde4ec",
            background: theme === "dark" ? "#0e1116" : "#ffffff",
            color: theme === "dark" ? "#e7ecf3" : "#111",
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Support
        </button>
      </div>
    </div>
  );
}

